import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Grid, Tooltip, Typography } from "@material-ui/core";
import { TurnedIn, Send } from "@material-ui/icons";

import { submitAcceptance } from "../../../../reducer/studentResearch/action";
import { uploadMultiFile } from "../../../../reducer/file/action";
import MultiFileManager from "../../../File/MultiFileManager/MultiFileManager";
import select from "../../../../utils/select";
import SectionCard from "../../utils/SectionCard";
import useDueWindow from "../../utils/useDueWindow";
import {
  STATUS_ACCEPTANCE_READY,
  STATUS_ACCEPTANCE_ADJUST_REQUEST,
  STATUS_PROGRESS_ACCEPTED,
  STATUS_CONDUCTED,
  DURATION_ONE_YEAR,
} from "../../../../constants/projectStatus";

// AcceptanceForm — thay cho ReportForm cũ.
// GV nộp báo cáo tổng kết (khi status sẵn sàng nộp).
// Việc chấm điểm + kết luận nghiệm thu + gán giải chuyển sang
// DepartmentLevelForm (refactor 2026-07-23 — gộp 3 bước cũ thành 1).
export default function AcceptanceForm({ detail, onDone }) {
  const dispatch = useDispatch();
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));

  // BE lưu mỗi field file là string đơn (hoặc nhiều URL nối "||"). FE giữ array
  // để hiển thị/edit qua MultiFileManager, join lại khi gửi BE.
  const parseUrls = (s) => (s ? String(s).split("||").filter(Boolean) : []);
  const [finalUrls, setFinalUrls] = useState(parseUrls(detail?.finalFileLink));
  const [finalNewFiles, setFinalNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  // Mirror window guards: "Nộp báo cáo tổng kết" → acceptStart/End;
  // BE còn check conferenceStart/End cho AssignCouncil(final) nhưng đó là
  // action ở admin tab (CouncilForm), không liên quan form này.
  const { check: checkDue } = useDueWindow(detail?.schoolYear);
  const acceptWindow = checkDue("acceptStartTime", "acceptEndTime");

  // Sync state khi user mở form cho row khác mà không đóng modal.
  // Cố ý chỉ depend vào detail.id để tránh ghi đè local edit khi user đang
  // sửa giữa chừng (deps thêm finalFileLink/etc sẽ trigger reset khi server
  // refetch sau action thành công).
  useEffect(() => {
    setFinalUrls(parseUrls(detail?.finalFileLink));
    setFinalNewFiles([]);
  }, [detail?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Upload pending files cho 1 field, merge với existing URLs, trả về string
  // "||"-joined. Commit kết quả vào state để UI chuyển newFiles → fileUrls.
  // BE endpoint /file/storage/multi/{id} chỉ accept type thuộc enum — module
  // này dùng "studentResearch".
  const commitFiles = async (existingUrls, newFiles, setUrls, setNewFiles) => {
    if (!newFiles || newFiles.length === 0) return existingUrls.join("||");
    const ownerId = profileDetail?.id || 0;
    const result = await dispatch(uploadMultiFile(ownerId, newFiles, "studentResearch"));
    if (result?.success && Array.isArray(result.data)) {
      const merged = [...existingUrls, ...result.data];
      setUrls(merged);
      setNewFiles([]);
      return merged.join("||");
    }
    return existingUrls.join("||");
  };

  // detail.status có thể là string hoặc array [{value, label}] (do
  // StudentProjectTable.rowRender). Chuẩn hoá về string value cuối.
  const extractStatus = (d) => {
    const s = d?.status;
    if (!s) return "";
    if (typeof s === "string") {
      const parts = s.split(",");
      return parts[parts.length - 1];
    }
    if (Array.isArray(s) && s.length > 0) {
      const last = s[s.length - 1];
      return typeof last === "object" ? last?.value : last;
    }
    return "";
  };
  const currentStatus = extractStatus(detail);

  const canSubmitAcceptance = (() => {
    const s = currentStatus;
    if (s === STATUS_ACCEPTANCE_READY || s === STATUS_ACCEPTANCE_ADJUST_REQUEST) return true;
    if (s === STATUS_PROGRESS_ACCEPTED) return true;
    if (s === STATUS_CONDUCTED && detail?.duration === DURATION_ONE_YEAR) return true;
    return false;
  })();

  const handleSubmit = async () => {
    if (finalUrls.length + finalNewFiles.length === 0) {
      alert("Vui lòng tải lên file báo cáo tổng kết.");
      return;
    }
    setSaving(true);
    try {
      const finalStr = await commitFiles(finalUrls, finalNewFiles, setFinalUrls, setFinalNewFiles);
      const ok = await dispatch(submitAcceptance(detail.id, finalStr));
      if (ok && onDone) onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box padding="16px 24px">
      <SectionCard icon={<TurnedIn />} title="Báo cáo tổng kết" marginTop={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>File báo cáo tổng kết</b>
            </Typography>
            <MultiFileManager
              fileUrls={finalUrls}
              setFileUrls={setFinalUrls}
              newFiles={finalNewFiles}
              setNewFiles={setFinalNewFiles}
              disabled={!canSubmitAcceptance}
            />
          </Grid>
        </Grid>
        {canSubmitAcceptance && (
          <Box marginTop={2} display="flex" justifyContent="flex-end">
            <Tooltip title={acceptWindow.isOpen ? "" : acceptWindow.reason || ""}>
              <span>
                <Button
                  variant="contained" color="primary"
                  onClick={handleSubmit} disabled={saving || !acceptWindow.isOpen}
                  startIcon={<Send />}
                >
                  {currentStatus === STATUS_ACCEPTANCE_ADJUST_REQUEST
                    ? "Gửi lại sau chỉnh sửa"
                    : "Nộp báo cáo tổng kết"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
        {!canSubmitAcceptance && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Chỉ có thể nộp báo cáo khi trạng thái là "Sẵn sàng nộp" hoặc "Yêu cầu chỉnh sửa".
          </Typography>
        )}
      </SectionCard>
    </Box>
  );
}
